import axios from "axios";
import { decryptAES } from "../../../Common/Axios/encrypt";
import { buildUpdateOrderPayload } from "../utils/buildUpdateOrderPayload";

export function usePaymentGateway({
  orderData,
  totalPayableAmount,
  burnPoints,
  loginV3Data,
  productReducer,
  razorPayOfferCode,
  payuOfferCode,
  MatchedCardMasterData,
  paymentConfig,
  setLoading,
  onUpdateOrder,
  onReset,
  isHtmlHostedPayu,
  updatedData,
  getOrderPayload,
  flow2,
  surlLocation
}) {
  const initiatePayment = () => {
    if (process.env.REACT_APP_IS_SKIP_PAYMENT_GATEWAY === "true") {
      const mockResponse = {
        razorpay_payment_id: orderData?.postOrderData?.[0]?.razorPayOrderId,
        razorpay_order_id: orderData?.postOrderData?.[0]?.razorPayOrderId,
        razorpay_signature: orderData?.postOrderData?.[0]?.razorPayOrderId,
      };
      onUpdateOrder(mockResponse, true);
      return;
    }
    const pg = orderData?.postOrderData?.[0]?.paymentPartner;
    if (pg === "PAYU") {
      const payuHostedIntegration = isHtmlHostedPayu;
      if (payuHostedIntegration) {
        // Use PayU Hosted Web Integration (redirect to PayU payment page)
        launchPayUHosted()
      } else {
        // Default: existing Bolt SDK flow
        launchPayU();
      }
    } else if (pg === "RAZORPAY") {
      launchRazorpay();
    }
  };

  const launchRazorpay = () => {
    if (!window.Razorpay) {
      console.error("Razorpay SDK not loaded");
      return;
    }
    setLoading(true);

    let lastFailedResponse = null;

    const options = {
      key: decryptAES(orderData?.postOrderData?.[0]?.apiKey),
      amount: totalPayableAmount.toFixed(2) * 100,
      order_id: orderData?.postOrderData?.[0]?.razorPayOrderId,
      currency: "INR",
      prefill: {
        name: loginV3Data?.memberProfile?.firstName,
        email: loginV3Data?.memberProfile?.emailId,
        contact: loginV3Data?.memberProfile?.mobileNumber,
      },
      offer_id:
        (burnPoints > 0 && !razorPayOfferCode ? MatchedCardMasterData?.razorpayPgCode : "") ||
        orderData?.postOrderData?.[0]?.offerId || "",
      ...(paymentConfig ? { config: JSON.parse(paymentConfig) } : {}),
      handler: function (response) {
        if (response?.razorpay_payment_id) {
          lastFailedResponse = null;
          setLoading(false);
          onUpdateOrder(response, true);
        }
      },
      modal: {
        ondismiss: function () {
          if (lastFailedResponse) {
            const paymentId = lastFailedResponse?.error?.metadata?.payment_id;
            onUpdateOrder(
              { razorpay_payment_id: paymentId, razorpay_order_id: lastFailedResponse?.error?.metadata?.order_id },
              true,
              false,
              true,
              false,
              "failed"
            );
          } else {
            onUpdateOrder(undefined, false, false, true, false, "Cancelled");
          }
          setLoading(false);
          onReset();
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
    rzp.on("payment.failed", function (response) {
      lastFailedResponse = response;
      setLoading(false);
    });
  };

  const launchPayU = () => {
    if (!window.bolt) {
      console.error("PayU Bolt SDK not loaded");
      return;
    }

    const userIdentifier =
      loginV3Data?.memberProfile?.mobileNumber ||
      loginV3Data?.memberProfile?.emailId;

    const data = {
      key: decryptAES(orderData?.postOrderData?.[0]?.apiKey),
      txnid: orderData?.postOrderData?.[0]?.orderId,
      hash: orderData?.postOrderData?.[0]?.razorPayOrderId,
      amount: totalPayableAmount.toFixed(2),
      firstname: `${loginV3Data?.memberProfile?.firstName || ""} ${loginV3Data?.memberProfile?.lastName || ""
        }`.trim(),
      email: loginV3Data?.memberProfile?.emailId,
      phone: loginV3Data?.memberProfile?.mobileNumber,
      productinfo: productReducer?.productReducer?.sku,
      store_card: 1,
      user_credentials: `${decryptAES(
        orderData?.postOrderData?.[0]?.apiKey,
      )}:${userIdentifier}`,
      offer_key:
        (burnPoints > 0 && !payuOfferCode ? MatchedCardMasterData?.payUPgCode : "") ||
        orderData?.postOrderData?.[0]?.offerId || "",
      surl: process.env.REACT_APP_PAYU_SUCCESS_URL,
      furl: process.env.REACT_APP_PAYU_FAILURE_URL,
    };

    window.bolt.launch(data, {
      responseHandler: function (BOLT) {
        if (BOLT.response.txnStatus.toLowerCase() === "SUCCESS".toLowerCase()) {
          setLoading(false);
          onUpdateOrder(BOLT.response, true);
        } else if (BOLT.response.txnStatus.toLowerCase() === "cancel".toLowerCase()) {
          onUpdateOrder(undefined, true, false, true, false, "failed");
          setLoading(false);
          onReset();
        } else {
          onUpdateOrder(undefined, true, false, true, false, "failed");
          setLoading(false);
          onReset();
        }
      },
      catchException: function (err) {
        onUpdateOrder(undefined, false, false, false, true, err);
        setLoading(false);
      },
    });
  };

  
 const setUpdateOrder = async (
    transaction,
    paymentVerify = false,
    pointEarn = false,
    orderCancelled = false,
    orderFailed = false,
    paymentFailedStatus,
  ) => {
    const existingPayload = sessionStorage.getItem("OrderPayloadSession");
    const payload = buildUpdateOrderPayload({
      existingPayload: existingPayload ? JSON.parse(existingPayload) : null,
      basePayload: getOrderPayload(),
      transaction,
      paymentVerify,
      orderCancelled,
      orderFailed,
      paymentFailedStatus,
      flow2,
      totalPayableAmount,
      orderId: orderData?.postOrderData?.[0]?.id,
      correlationId: orderData?.postOrderData?.[0]?.correlationId,
      hash: orderData?.postOrderData?.[0]?.razorPayOrderId,
      key: orderData?.postOrderData?.[0]?.apiKey,
      cancelUrl: surlLocation,
    });
    sessionStorage.setItem("OrderPayloadSession_post", JSON.stringify(payload));
  };


  const launchPayUHosted = async () => {
    setLoading(true);

    const postUrl =
      process.env.REACT_APP_P_M === "production"
        ? "https://secure.payu.in/_payment"
        : "https://test.payu.in/_payment";

    const userIdentifier =
      loginV3Data?.memberProfile?.mobileNumber ||
      loginV3Data?.memberProfile?.emailId;

    const decryptedKey = decryptAES(orderData?.postOrderData?.[0]?.apiKey);
    const txnid = orderData?.postOrderData?.[0]?.orderId;
    const amount = totalPayableAmount.toFixed(2);
    const firstname = `${loginV3Data?.memberProfile?.firstName || ""} ${loginV3Data?.memberProfile?.lastName || ""}`.trim();
    const email = loginV3Data?.memberProfile?.emailId;
    const phone = loginV3Data?.memberProfile?.mobileNumber;
    const productinfo = productReducer?.productReducer?.sku;
    // Hash should be produced by server and provided in orderData for security
    const hash = orderData?.postOrderData?.[0]?.razorPayOrderId
    const store_card = 1;
    const user_credentials = `${decryptAES(
      orderData?.postOrderData?.[0]?.apiKey,
    )}:${userIdentifier}`;
    const offer_key =
      (burnPoints > 0 && !payuOfferCode ? MatchedCardMasterData?.payUPgCode : "") ||
      orderData?.postOrderData?.[0]?.offerId || "";

     
    // const appOrigin = (typeof window !== 'undefined' && window.location && window.location.origin)
    //   ? window.location.origin
    //   : (surlLocation || "")  

    // Ensure success/failure URLs point back to this application origin so PayU redirects to the SPA routes
    const surl = orderData?.postOrderData?.[0]?.SURL;
    const furl = orderData?.postOrderData?.[0]?.FURL;
    const cancelUrl = new URL(surlLocation || window.location.href, window.location.origin);
    cancelUrl.searchParams.set("paymentCancelled", "true");
    const curl = cancelUrl.toString();

    setUpdateOrder();
    sessionStorage.setItem("cancelUrl", JSON.stringify(curl));
    const checkoutSourceData = Array.isArray(updatedData) && updatedData.length
      ? updatedData
      : productReducer?.arrayData?.arrayData?.length
        ? productReducer.arrayData.arrayData
        : productReducer?.modalProductData || [];
    sessionStorage.setItem(
      "PayUHostedCheckoutData",
      JSON.stringify({ savedAt: Date.now(), sourceData: checkoutSourceData }),
    );
    try {
      // const { data } = await axios.post("http://localhost:5000/api/payu/initiate", {
      //   amount,
      //   firstname: firstname,
      //   email: email,
      //   phone: phone,
      //   productinfo: productinfo,
      // });

      const form = document.createElement("form");
      form.method = "POST";
      form.action = postUrl;

      const params = {
        key: decryptedKey,
        txnid,
        amount,
        firstname,
        email,
        phone,
        productinfo,
        surl,
        furl,
        hash,
        store_card,
        user_credentials,
        offer_key,
        curl,
      };

      Object.entries(params).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error("Error launching PayU Hosted flow", err);
      setLoading(false);
      // Preserve current behavior: surface error to onUpdateOrder so caller can handle
      onUpdateOrder(undefined, false, false, false, true, err);
    }
  };

  return { initiatePayment };
}
